/**
 * Auth session helpers.
 *
 * Primary path: email + password (MVP-friendly, no email round-trip).
 * Fallback path: magic link (preserved for users who'd rather not pick a
 * password, and as a self-serve "forgot password" recovery).
 *
 * Pure functions. React-free. Wrap in hooks (see `src/hooks/useSession.ts`)
 * when you need reactive state.
 */

import { supabase } from '@/lib/supabase/client';
import type { Session } from '@supabase/supabase-js';

export async function getSession(): Promise<Session | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

/**
 * Send a magic-link email. The link in the email lands at `redirectTo`,
 * which should be the app's `/auth/callback?next=<intended-path>`.
 */
export async function sendMagicLink(opts: {
  email: string;
  redirectTo: string;
}): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email: opts.email,
    options: {
      emailRedirectTo: opts.redirectTo,
      // Allow creating new accounts on first sign-in. Set false here if you
      // want to require an out-of-band invite first.
      shouldCreateUser: true,
    },
  });
  if (error) throw error;
}

/**
 * Sign in with email + password.
 *
 * Throws an Error whose `name` is preserved (`AuthApiError`, etc.) so the
 * caller can distinguish "wrong password" from "user doesn't exist".
 * Supabase returns the same generic "Invalid login credentials" message for
 * both cases by design (anti-enumeration). Callers that want
 * sign-in-or-create UX should catch the error and fall back to
 * `signUpWithPassword`.
 */
export async function signInWithPassword(opts: {
  email: string;
  password: string;
}): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({
    email: opts.email,
    password: opts.password,
  });
  if (error) throw error;
}

/**
 * Create a new account with email + password.
 *
 * If "Confirm email" is enabled in the Supabase dashboard, the user will
 * receive a confirmation email and `session` will be null until they click
 * it. For an MVP, turn that setting OFF so signup logs the user in
 * immediately — no inbox round-trip.
 */
export async function signUpWithPassword(opts: {
  email: string;
  password: string;
}): Promise<void> {
  const { error } = await supabase.auth.signUp({
    email: opts.email,
    password: opts.password,
  });
  if (error) throw error;
}

/**
 * Convenience: try sign-in, fall back to sign-up if the user doesn't exist.
 *
 * Supabase masks user-not-found as "Invalid login credentials", so we can't
 * distinguish it from "wrong password" reliably. Strategy: try sign-in
 * first; if it fails, try sign-up; if sign-up also fails because the user
 * already exists, the original error was "wrong password" — surface that.
 */
export async function signInOrSignUpWithPassword(opts: {
  email: string;
  password: string;
}): Promise<{ created: boolean }> {
  try {
    await signInWithPassword(opts);
    return { created: false };
  } catch (signInErr) {
    try {
      await signUpWithPassword(opts);
      return { created: true };
    } catch (signUpErr) {
      const msg =
        signUpErr instanceof Error ? signUpErr.message.toLowerCase() : '';
      // If signup failed because the account already exists, the sign-in
      // failure was a real wrong-password — surface that instead.
      if (
        msg.includes('already registered') ||
        msg.includes('already exists') ||
        msg.includes('user already')
      ) {
        throw signInErr;
      }
      throw signUpErr;
    }
  }
}

/**
 * Initiate a Google OAuth sign-in.
 *
 * Flow: browser → Google consent screen → Supabase callback
 * (`https://<project>.supabase.co/auth/v1/callback`) → app (`redirectTo`).
 * The Supabase browser client has `detectSessionInUrl: true`, so the
 * landing page (`/auth/callback`) picks up the OAuth code automatically
 * and fires `SIGNED_IN` — exactly like a magic-link round trip.
 *
 * `redirectTo` MUST be in the Supabase Redirect URLs allowlist
 * (Authentication → URL Configuration). Always build it from
 * `buildCallbackUrl(nextPath)` so the redirect returns to the *current*
 * origin (works on local, preview, and production without code change).
 *
 * On the happy path the browser navigates away before this promise
 * settles — the only way it resolves locally is if Supabase rejects the
 * request (provider disabled, allowlist mismatch, etc.), and we surface
 * that as a thrown error.
 */
export async function signInWithGoogle(opts: {
  redirectTo: string;
}): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: opts.redirectTo,
      // Defaults to 'openid email profile' — exactly what we need for
      // the handle_new_user trigger to create a profile row. No extra
      // Google scopes (no Drive/Calendar/etc.).
    },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export function buildCallbackUrl(nextPath: string): string {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : '';
  const safeNext = nextPath.startsWith('/') ? nextPath : '/';
  return `${origin}/auth/callback?next=${encodeURIComponent(safeNext)}`;
}
